import {
  Action,
  Badge,
  Card,
  cn,
  Disclosure,
  Icon,
  RelativeDateTime,
  SimpleSummary,
  SupportedIcon,
} from 'corso-admin-ui';
import {
  crewClaimRollupStatusCodeName,
  crewClaimTypeEnumName,
} from 'corso-types';
import { formatter } from 'corso-web-lib';
import { z } from 'zod';
import Paginator from '~/components/ui/Paginator';
import { useEnabledClaimType } from '~/hooks/useEnabledClaimType';
import { usePaginate } from '~/hooks/usePaginate';
import { useStoreCustomer } from '~/hooks/useStoreCustomer';
import { useParsedStickySearchParams } from '~/providers/StickySearchParams';
import { useStoreId } from '~/providers/StoreUserProvider';
import { shippingClaimStatusName } from '~/utils/enumNameMaps';
import { ErrorResponse } from '~/utils/errorResponse';

// TODO review all "status card"-like components and unify the design and implementation
export function InsightStatusCard({
  icon,
  title,
  detail,
  spinner = false,
}: {
  icon: SupportedIcon;
  title: string;
  detail: string;
  spinner?: boolean;
}) {
  return (
    <Card flat>
      <div className="flex flex-col items-center justify-center gap-4 text-center">
        <Icon
          icon={icon}
          className={cn('size-6 text-neutral-400', spinner && 'animate-spin')}
        />
        <p className="text-sm font-medium text-neutral-700">{title}</p>
        <p className="text-xs text-balance text-neutral-600">{detail}</p>
      </div>
    </Card>
  );
}

const PER_PAGE = 5;

// TODO revisits lists and evaluate deduplicating content components
type StoreCustomer = NonNullable<ReturnType<typeof useStoreCustomer>['data']>;

function CustomerOrderList({ orders }: { orders: StoreCustomer['orders'] }) {
  const storeId = useStoreId();
  const pagination = usePaginate({
    items: orders,
    perPage: PER_PAGE,
  });

  if (!orders.length) {
    return (
      <>
        <Card.Heading>Orders</Card.Heading>
        <InsightStatusCard
          icon="order"
          title="No Orders Found"
          detail="When orders are placed, they will appear here for easier claim management."
        />
      </>
    );
  }

  return (
    <>
      <Card.Line>
        <Card.Heading>
          Orders <Badge>{formatter.count(orders.length)}</Badge>
        </Card.Heading>
        <Paginator pagination={pagination} />
      </Card.Line>
      <ul className="flex flex-col gap-px overflow-clip rounded-md border border-neutral-200 bg-neutral-200 *:bg-white">
        {pagination.pageItems.map((order) => {
          const claimCount =
            order.claims.length + order.shipProtectClaims.length;

          if (!claimCount) {
            return (
              <li key={order.idFromPlatform}>
                <div className="flex flex-col items-start p-2">
                  <Action
                    variant="link"
                    to={`/${storeId}/orders/lookup/${order.idFromPlatform}`}
                  >
                    {order.orderNo}
                  </Action>
                  <RelativeDateTime
                    dateTime={order.createdOn}
                    className="text-xs text-neutral-500"
                    full
                  />
                </div>
              </li>
            );
          }
          return (
            <li key={order.idFromPlatform}>
              <Disclosure
                renderSummary={
                  <SimpleSummary>
                    <div className="flex flex-col items-start">
                      <div className="flex items-center gap-2">
                        <Action
                          variant="link"
                          to={`/${storeId}/orders/lookup/${order.idFromPlatform}`}
                          className="text-sm"
                        >
                          {order.orderNo}
                        </Action>
                        <Badge variant="info">
                          {formatter.count(claimCount)}{' '}
                          {formatter.plural(claimCount, {
                            one: 'Claim',
                            other: 'Claims',
                          })}
                        </Badge>
                      </div>
                      <RelativeDateTime
                        dateTime={order.createdOn}
                        className="text-xs text-neutral-500"
                        full
                      />
                    </div>
                  </SimpleSummary>
                }
              >
                <ul className="space-y-1 pb-2 pl-5">
                  {order.claims.map((claim) => (
                    <li key={`crew-${claim.id}`}>
                      <Action
                        variant="link"
                        to={`/${storeId}/claims/${claim.claimType}/${claim.id}`}
                        className="text-sm"
                      >
                        #{claim.externalId}
                      </Action>{' '}
                      <Badge>
                        {crewClaimTypeEnumName[claim.claimType]}
                        &nbsp;&middot;&nbsp;
                        {
                          crewClaimRollupStatusCodeName[
                            claim.claimRollupStatusCode
                          ]
                        }
                      </Badge>
                    </li>
                  ))}
                  {order.shipProtectClaims.map((claim) => (
                    <li key={`gsp-${claim.id}`}>
                      <Action
                        variant="link"
                        to={`/${storeId}/claims/shipping/${claim.id}`}
                        className="text-sm"
                      >
                        #{claim.id}
                      </Action>{' '}
                      <Badge>
                        Shipping&nbsp;&middot;&nbsp;
                        {shippingClaimStatusName[claim.status]}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </Disclosure>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function CustomerRegistrationList({
  registrations,
}: {
  registrations: StoreCustomer['registrations'];
}) {
  const { isRegistrationEnabled } = useEnabledClaimType();
  const storeId = useStoreId();
  const pagination = usePaginate({
    items: registrations,
    perPage: PER_PAGE,
  });

  if (!isRegistrationEnabled) return null;

  if (!registrations.length) {
    return (
      <>
        <Card.Heading>Registrations</Card.Heading>
        <InsightStatusCard
          icon="registration"
          title="No Registrations Found"
          detail="This customer has not registered any products yet."
        />
      </>
    );
  }

  return (
    <>
      <Card.Line>
        <Card.Heading>
          Registrations <Badge>{formatter.count(registrations.length)}</Badge>
        </Card.Heading>
        <Paginator pagination={pagination} />
      </Card.Line>
      <ul className="flex flex-col gap-px overflow-clip rounded-md border border-neutral-200 bg-neutral-200 *:bg-white">
        {pagination.pageItems.map((registration) => (
          <li key={registration.id}>
            <div className="flex flex-col items-start p-2">
              <div className="flex items-center gap-2">
                <Action
                  variant="link"
                  to={`/${storeId}/registrations/${registration.id}`}
                  className="text-sm"
                >
                  #{registration.id}
                </Action>
                <Badge>{registration.registrationChannelName}</Badge>
              </div>
              <RelativeDateTime
                dateTime={registration.createdOn}
                className="text-xs text-neutral-500"
                full
              />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

export default function CustomerInsight() {
  const { customerEmail } =
    useParsedStickySearchParams(z.object({ customerEmail: z.email() })) ?? {};

  const {
    data: storeCustomer,
    isPending,
    isError,
    error,
  } = useStoreCustomer(customerEmail);

  if (!customerEmail)
    return (
      <InsightStatusCard
        icon="help"
        title="Email Not Detected"
        detail="Reload to try again, or search for anything."
      />
    );

  if (isPending)
    return (
      <InsightStatusCard
        icon="spinner"
        spinner
        title="Loading Customer"
        detail="Please wait while we load the customer."
      />
    );

  if (isError) {
    if (error instanceof ErrorResponse && error.response.status === 404) {
      return (
        <InsightStatusCard
          icon="help"
          title="No Customer Found"
          detail="No customer data available in Corso."
        />
      );
    }

    console.error('CustomerInsight error:', error);
    return (
      <InsightStatusCard
        icon="error"
        title="Error Loading Customer"
        detail="Failed to load customer data. Please try again later."
      />
    );
  }

  const name =
    `${storeCustomer.firstName ?? ''} ${storeCustomer.lastName ?? ''}`.trim() ||
    null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Icon icon="customer" />
        <p className="truncate text-xs text-nowrap text-neutral-700">
          {name && <>{name}&nbsp;&middot;&nbsp;</>}
          {storeCustomer.email}
        </p>
      </div>
      {/* // ? might want to add stat cards here about a customer so on desktop they're on the right, but the second column goes below on mobile (alike claims) */}
      <CustomerOrderList orders={storeCustomer.orders} />
      <CustomerRegistrationList registrations={storeCustomer.registrations} />
    </div>
  );
}
